import { fetchJSON } from "lib/fetch";
import isEmpty from "lodash/isEmpty";
import {
    IListItemsRequest,
    IItemsPermissionsRequest,
    IMoveItemRequest,
    IRestoreItemRequest,
    IDeleteItemRequest,
    IAddItemRequest,
    IRenameItemRequest,
    IUpdateItemPermissionRequest,
    IToggleStarredItemRequest,
} from "../types/space-item";
import siteConfig from "site-config";
import { stringify } from "querystring";
import { flattenParams } from "helpers/query-helpers";
import { generateShortcutName } from "../utils/space-item";
import { ErrorCodes, MIN_RETRY_FOR_CREATE_MANUAL_SHORTCUT } from "../utils/constants";
import { IAllocationDimension, normalizeCollectionAllocationDimensionJS } from "helpers/allocation-dimension";
import { ICollectionBrandProperties } from "helpers/collection-objects";

export interface ISpaceItemService {
    getItems: (
        merchantId: string,
        itemId: string,
        params?: IListItemsRequest
    ) => Promise<nuorder.folder.v1.ListItemsResponse>;
    getItem: (merchantId: string, itemId: string) => Promise<nuorder.folder.v1.Item>;
    getItemPermissions?: (
        merchantId: string,
        itemId: string,
        params?: IItemsPermissionsRequest
    ) => Promise<nuorder.folder.v1.ListPermissionsResponse>;
    deleteItem: ({ merchantId, itemId }: IDeleteItemRequest) => Promise<any>;
    addItem: ({ merchantId, rootId, name, kind }: IAddItemRequest) => Promise<any>;
    renameItem?: ({ merchantId, itemId, name }: IRenameItemRequest) => Promise<any>;
    toggleStarredItem?: ({ merchantId, itemId, addToStarred }: IToggleStarredItemRequest) => Promise<any>;
    moveItem?: (payload: IMoveItemRequest) => Promise<nuorder.folder.v1.Item>;
    restoreItem?: (payload: IRestoreItemRequest) => Promise<nuorder.folder.v1.Item>;
    getUserSharedItems: (
        merchantId: string,
        memberId: string,
        params?: IListItemsRequest
    ) => Promise<nuorder.folder.v1.ListItemsResponse>;
    getStarredItems?: (merchantId: string, params?: IListItemsRequest) => Promise<nuorder.folder.v1.ListItemsResponse>;
    getBulkProducts?: (payload: any) => Promise<any>;
    updateItemPermission?: ({
        merchantId,
        itemId,
        memberId,
        memberMerchantId,
        type,
        role,
        originalRole,
    }: IUpdateItemPermissionRequest) => Promise<nuorder.folder.v1.Permission>;
    deleteItemPermission?: ({
        merchantId,
        itemId,
        permissionId,
    }: {
        merchantId: string;
        itemId: string;
        permissionId: string;
    }) => Promise<any>;
    createItemPermission?: ({
        merchantId,
        itemId,
        role,
        memberIds,
        memberMerchantIds,
        kind,
    }: {
        merchantId: string;
        itemId: string;
        role: string;
        memberIds: string[];
        memberMerchantIds: string[];
        kind: string | number;
    }) => Promise<any[]>; // will return success or error mix
    fetchCollectionItem: (
        collectionId?: string,
        merchantId?: string,
        isCopying?: boolean
    ) => Promise<IAllocationDimension[]>;
    fetchCollectionBrandItem: (
        collectionId?: string,
        merchantId?: string,
        isCopying?: boolean
    ) => Promise<ICollectionBrandProperties[]>;
}

export const spaceItem: ISpaceItemService = {
    getItems: (merchantId: string, itemId: string, params?: IListItemsRequest) =>
        fetchJSON(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/children${
                !isEmpty(params) ? `?${stringify(flattenParams(params))}` : ""
            }`
        ),
    getItem: (merchantId: string, itemId: string) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}`),
    getItemPermissions: (merchantId: string, itemId: string, params?: IItemsPermissionsRequest) =>
        fetchJSON(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/permissions?indicate_status=true${
                !isEmpty(params) ? `&${stringify(params)}` : ""
            }`
        ),
    deleteItem: ({ merchantId, itemId }: IDeleteItemRequest) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}?recursive_delete=true`, {
            method: "DELETE",
        }),
    addItem: (params: IAddItemRequest) => {
        const retryForManualShortcut = 1;

        if (params.isShortcutItem) {
            params.name = generateShortcutName({
                item: params.item,
                shouldIncludeDateFormat: params.belongsToRollupItem,
            });
        }
        return createItemAjaxRequest(params, retryForManualShortcut);
    },
    renameItem: ({ merchantId, itemId, name }: IRenameItemRequest) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}`, {
            method: "PUT",
            body: JSON.stringify({ name, field_mask: { paths: ["name"] } }),
        }),
    toggleStarredItem: ({ merchantId, itemId, addToStarred }: IToggleStarredItemRequest) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}`, {
            method: "PUT",
            body: JSON.stringify({ starred: addToStarred, field_mask: { paths: ["starred"] } }),
        }),
    moveItem: ({ merchantId, itemId, parentId: parent }: IMoveItemRequest) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}`, {
            method: "PATCH",
            body: JSON.stringify({ parent }),
        }),
    restoreItem: ({ merchantId, itemId }: IRestoreItemRequest) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/restore`, {
            method: "POST",
            useCustomErrorCode: true,
        }),
    getUserSharedItems: (merchantId: string, memberId: string, params?: IListItemsRequest) =>
        fetchJSON(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/members/${memberId}/space/sharedWithMember${
                !isEmpty(params) ? `?${stringify(flattenParams(params))}` : ""
            }`
        ),
    getBulkProducts: ({ items }) => {
        const params = "?board_ids=" + items.join("&board_ids=");
        return fetchJSON(`${siteConfig.goAPI}/v1/boards/bulk/products${params}`);
    },
    getStarredItems: (merchantId: string, params?: IListItemsRequest) =>
        fetchJSON(
            `${siteConfig.goAPI}/v1/merchants/${merchantId}/items/starred${
                !isEmpty(params) ? `?${stringify(flattenParams(params))}` : ""
            }`
        ),
    updateItemPermission: ({
        merchantId,
        itemId,
        memberId,
        memberMerchantId,
        type,
        role,
        originalRole,
    }: IUpdateItemPermissionRequest) => {
        return fetchJSON(`${siteConfig.goAPI}/v1/space/items/${itemId}/replace_permission`, {
            method: "PUT",
            body: JSON.stringify({
                item_id: itemId,
                grantee: {
                    kind: type,
                    merchant_id: memberMerchantId,
                    member_id: memberId,
                },
                role: originalRole,
                new_role: role,
                merchant_id: merchantId,
            }),
        });
    },
    deleteItemPermission: ({
        merchantId,
        itemId,
        permissionId,
    }: {
        merchantId: string;
        itemId: string;
        permissionId;
    }) =>
        fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/permissions/${permissionId}`, {
            method: "DELETE",
        }),
    createItemPermission: ({
        merchantId,
        itemId,
        role,
        memberIds,
        memberMerchantIds,
        kind,
    }: {
        merchantId: string;
        itemId: string;
        role: string;
        memberIds: string[];
        memberMerchantIds: string[];
        kind: string | number;
    }) => {
        const CreatePermissions = memberIds.map((memberId, index) => {
            return fetchJSON(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/permissions`, {
                method: "POST",
                body: JSON.stringify({
                    grantee: {
                        kind,
                        merchant_id: memberMerchantIds[index],
                        member_id: memberId,
                    },
                    role,
                }),
            }).catch((e) => e);
        });

        return Promise.all(CreatePermissions);
    },
    fetchCollectionItem: (
        collectionId: string,
        merchantId: string,
        isCopying: boolean
    ): Promise<IAllocationDimension[]> => {
        return fetchJSON(
            `${siteConfig.goAPI}/v1/collections/${collectionId}?with_snapshot=true&merchant_id=${merchantId}`,
            {
                method: "GET",
                noDomain: true,
            }
        ).then((result: any) => {
            const allocationDimensions = [];
            const collectionBrandProperties = [];

            if (null !== result.collection?.brand_collection_properties) {
                collectionBrandProperties.push(result.collection?.brand_collection_properties);
            }
            const allocationDimensionsData = result.collection.snapshot?.allocation_dimensions || {};
            const allocationDimensionOrders = result.collection.item_allocation_dimension_order || [];

            allocationDimensionOrders.forEach((allocationDimensionId) => {
                const allocationDimension = allocationDimensionsData[allocationDimensionId];
                if (allocationDimension) {
                    const allocationDimensionObj = allocationDimension.mutable_mixed.objects;
                    const allocationDimensionRef = allocationDimension.ref;
                    const normalized = normalizeCollectionAllocationDimensionJS(
                        allocationDimensionRef,
                        allocationDimensionObj
                    );
                    allocationDimensions.push(normalized);
                }
            });

            return allocationDimensions;
        });
    },
    fetchCollectionBrandItem: (
        collectionId: string,
        merchantId: string,
        isCopying: boolean
    ): Promise<ICollectionBrandProperties[]> => {
        return fetchJSON(
            `${siteConfig.goAPI}/v1/collections/${collectionId}?with_snapshot=true&merchant_id=${merchantId}`,
            {
                method: "GET",
                noDomain: true,
            }
        ).then((result: any) => {
            const collectionBrandProperties = [];
            if (null !== result.collection?.brand_collection_properties) {
                collectionBrandProperties.push(result.collection?.brand_collection_properties);
            }
            return collectionBrandProperties;
        });
    },
};

const createItemAjaxRequest = (params: IAddItemRequest, retryForManualShortcut) =>
    fetchJSON(`${siteConfig.goAPI}/v1/merchants/${params.merchantId}/space/items/${params.rootId}/children`, {
        method: "POST",
        body: JSON.stringify({
            name: params.name,
            kind: params.kind,
            ...(params.targetItemId
                ? {
                      shortcutFacet: {
                          targetItemId: params.targetItemId,
                      },
                  }
                : {}),
        }),
        useCustomErrorCode: true,
    }).catch((error) => {
        const alreadyExistsError = error.code === ErrorCodes.ALREADY_EXISTS_ITEM_WITH_SAME_NAME;

        retryForManualShortcut += 1;
        if (alreadyExistsError && params.isShortcutItem) {
            // Should only include milliseconds for manual shortcut when the retry is greater than MIN_RETRY_FOR_CREATE_MANUAL_SHORTCUT(2)
            params.name = generateShortcutName({
                item: params.item,
                includeMilliseconds:
                    params.belongsToRollupItem || retryForManualShortcut > MIN_RETRY_FOR_CREATE_MANUAL_SHORTCUT,
                shouldIncludeDateFormat: true,
            });
            return createItemAjaxRequest(params, retryForManualShortcut);
        }
        throw error;
    });
